import i18n from "i18next";
import { useCallback } from "react";

import { store } from "../../core/setup/redux/store";

type CachedQuery = { endpointName?: string; originalArgs?: unknown; data?: { items?: { key: string; value: string }[] } };

const stripQuery = (url: string) => url.split("?")[0];

/**
 * Looks up the translation key for a given value in the cached options of the field's own datasource
 * (`urlToFetchOptions`, as loaded by `SelectDynamic`). Scoping to the field's datasource matters because
 * different datasources share ids - e.g. site 122 and terminal type 122.
 */
const findOptionTranslationKey = (value: string, datasourceUrl: string): string | undefined => {
    const state = store.getState() as unknown as Record<string, unknown>;
    const apiState = state.authApi as { queries?: Record<string, CachedQuery> } | undefined;
    const queries = apiState?.queries;
    if (!queries) return undefined;

    const datasourcePath = stripQuery(datasourceUrl);
    for (const entry of Object.values(queries)) {
        // The cached URL may be prefixed with the data group (`/data-groups/{code}`) and carry query params.
        if (entry?.endpointName !== "loadConfig" || typeof entry.originalArgs !== "string") continue;
        if (!stripQuery(entry.originalArgs).endsWith(datasourcePath)) continue;
        const matched = entry.data?.items?.find((item) => String(item.value) === value);
        if (matched?.key) return matched.key;
    }
    return undefined;
};

export const useFormatFilterValue = () => {
    return useCallback((value: unknown, datasourceUrl?: string): string => {
        if (typeof value === "boolean") return i18n.t(value ? "common.yes" : "common.no");
        if (datasourceUrl && (typeof value === "string" || typeof value === "number")) {
            const key = findOptionTranslationKey(String(value), datasourceUrl);
            return key ? i18n.t(key) : String(value);
        }
        return String(value);
    }, []);
};
